// ec-card.js — "শক্তির রিপোর্ট কার্ড", drawn by hand with the native Canvas API.
//
// A COMPLETION REWARD, not a sharing feature. Peak-End: the narrative ends on an
// artifact the user keeps. There is deliberately no share button, no social
// network, no "viral" copy anywhere in this file or its markup — just a
// download.
//
// NO LIBRARIES. Not html2canvas, not anything. Every pixel below is a Canvas
// 2D call. The card is the same device as the page: charcoal chassis, gold
// top-rule, corner screws, sage LCD, brand plate.
//
// COLOUR: read from the SAME :root tokens the stylesheet uses, resolved through
// a probe element so that color-mix() derivations resolve too. Re-theming
// ec-styles.css re-themes this card with no edit here.
//
// FAILING GRACEFULLY is a hard requirement: if fonts never load, if Bengali
// cannot be rendered, if toBlob is unavailable or throws — the card is skipped
// with a plain message and the results page keeps working. It must never throw
// into the page.

(function (root) {
  'use strict';

  // Square. The content is a title, one LCD and two rows; at 4:5 that left a
  // third of the card empty, which reads as a rendering bug rather than as
  // breathing room. 1080² also reads well on a phone and prints cleanly.
  var W = 1080;
  var H = 1080;

  var els = {};
  var booted = false;
  var lastObjectUrl = null;

  function $(sel, scope) {
    return (scope || document).querySelector(sel);
  }

  function i18n() {
    return root.EC_I18N;
  }

  function label(obj) {
    return i18n().pick(obj);
  }

  /* ------------------------------------------------------------- tokens */

  /**
   * Resolve a CSS custom property to a real colour string.
   *
   * Reading the property directly is not enough: unregistered custom properties
   * compute to their raw token stream, so a derived token comes back as the
   * literal text "color-mix(in srgb, …)", which canvas cannot use. Assigning it
   * to `color` on a probe element and reading the computed style forces the
   * browser to resolve it to an rgb() triple.
   */
  var probe = null;
  function token(name, fallback) {
    try {
      if (!probe) {
        probe = document.createElement('span');
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:absolute;left:-9999px;top:-9999px;pointer-events:none';
        document.body.appendChild(probe);
      }
      probe.style.color = '';
      probe.style.color = 'var(' + name + ')';
      var v = getComputedStyle(probe).color;
      // An unresolvable var() leaves `color` at its inherited value; treat a
      // missing/blank result as "use the fallback" rather than painting wrong.
      if (v && v !== 'rgba(0, 0, 0, 0)') return v;
    } catch (e) {
      /* fall through */
    }
    return fallback;
  }

  function palette() {
    return {
      chassis: token('--ec-chassis', '#232F3E'),
      chassisDeep: token('--ec-chassis-deep', '#161E28'),
      chassisEdge: token('--ec-chassis-edge', '#55606F'),
      brand: token('--ec-brand', '#FED700'),
      savings: token('--ec-savings', '#6ABDB8'),
      danger: token('--ec-danger-lit', '#EA7C7A'),
      warning: token('--ec-warning-lit', '#ED8F87'),
      lcdBg: token('--ec-lcd-bg', '#AEB99C'),
      lcdInk: token('--ec-lcd-ink', '#3A4030'),
      lcdBgAlarm: token('--ec-lcd-bg-alarm', '#3A4030'),
      lcdInkAlarm: token('--ec-lcd-ink-alarm', '#D8DFC8'),
      onChassis: token('--ec-on-chassis', '#F5F6F7'),
      onChassisDim: token('--ec-on-chassis-dim', '#ACB0B6')
    };
  }

  /* -------------------------------------------------------------- fonts */

  var BN_UI = '"Hind Siliguri", "Baloo Da 2", "Nirmala UI", sans-serif';
  var BN_DISPLAY = '"Baloo Da 2", "Hind Siliguri", "Nirmala UI", sans-serif';
  // English mode draws with the system sans stack rather than loading a new
  // webfont just for the card — no extra network request, and every OS this
  // audience runs already ships one of these.
  var LATIN_UI = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
  var LATIN_DISPLAY = '"Segoe UI", Roboto, Helvetica, Arial, sans-serif';
  var SEG = '"DSEG7 Classic", "Space Mono", monospace';
  var MONO = '"Space Mono", monospace';

  /** UI/display font stacks for the active locale — Bengali gets the Bengali
   *  webfont pairing, English draws in the system sans stack. */
  function fontsFor(locale) {
    return locale === 'bn'
      ? { ui: BN_UI, display: BN_DISPLAY }
      : { ui: LATIN_UI, display: LATIN_DISPLAY };
  }

  /**
   * Can this device actually draw Bengali?
   *
   * Tofu boxes all share one advance width, so if two visually different
   * Bengali letters measure identically AND match an unmapped codepoint, the
   * text would render as boxes and the card is worthless. Cheap, and it catches
   * the only case where drawing would produce something embarrassing.
   *
   * Only consulted when the active locale is Bengali — the English card draws
   * no Bengali glyphs at all (beyond the ৳ mark, which every fallback in SEG/
   * MONO already carries), so this check would otherwise fail an English card
   * over a font gap that can never actually show up on it.
   */
  function bengaliRenders(ctx) {
    try {
      ctx.save();
      ctx.font = '48px ' + BN_UI;
      var a = ctx.measureText('শ').width;
      var b = ctx.measureText('ক').width;
      var tofu = ctx.measureText('￿').width;
      ctx.restore();
      if (!a || !b) return false;
      var identical = Math.abs(a - b) < 0.01 && Math.abs(a - tofu) < 0.01;
      return !identical;
    } catch (e) {
      return false;
    }
  }

  /**
   * Wait for the webfonts before drawing, so glyphs never fall back to tofu
   * mid-render. `document.fonts.ready` resolves once loading settles; the race
   * is a hard cap so a hung font request cannot hang the button forever.
   */
  function fontsReady() {
    var timeout = new Promise(function (resolve) { setTimeout(resolve, 3000); });
    if (!document.fonts || !document.fonts.ready) return timeout;
    return Promise.race([document.fonts.ready, timeout]);
  }

  /* ------------------------------------------------------------ drawing */

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + w - r, y);
    ctx.quadraticCurveTo(x + w, y, x + w, y + r);
    ctx.lineTo(x + w, y + h - r);
    ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
    ctx.lineTo(x + r, y + h);
    ctx.quadraticCurveTo(x, y + h, x, y + h - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  }

  function screw(ctx, x, y, c) {
    ctx.fillStyle = c;
    ctx.beginPath();
    ctx.arc(x, y, 5, 0, Math.PI * 2);
    ctx.fill();
  }

  /** International digits with thousands separators, matching the page. */
  function fmt(n) {
    return Math.round(n).toLocaleString('en-US');
  }

  /**
   * Draw the whole card. Pure: takes data, paints a canvas, returns nothing.
   */
  function draw(ctx, data, p) {
    var pad = 64;
    var F = fontsFor(data.locale);

    // ---- chassis
    ctx.fillStyle = p.chassis;
    ctx.fillRect(0, 0, W, H);

    // ---- gold top-rule, the logo's own weight
    ctx.fillStyle = data.severity === 'danger' ? p.danger : p.brand;
    ctx.fillRect(0, 0, W, 12);

    // ---- corner screws
    screw(ctx, 34, 46, p.chassisEdge);
    screw(ctx, W - 34, 46, p.chassisEdge);
    screw(ctx, 34, H - 34, p.chassisEdge);
    screw(ctx, W - 34, H - 34, p.chassisEdge);

    // ---- brand plate
    ctx.textBaseline = 'alphabetic';
    ctx.textAlign = 'right';
    ctx.font = '400 22px ' + MONO;
    ctx.fillStyle = p.onChassisDim;
    ctx.fillText('· MTR-01', W - pad, 72);
    var tail = ctx.measureText('· MTR-01').width;
    ctx.font = '700 22px ' + MONO;
    ctx.fillStyle = p.brand;
    ctx.fillText('alphaeshop ', W - pad - tail, 72);

    // ---- title
    ctx.textAlign = 'left';
    ctx.font = '800 54px ' + F.display;
    ctx.fillStyle = p.onChassis;
    ctx.fillText(data.title, pad, 132);

    ctx.font = '400 26px ' + F.ui;
    ctx.fillStyle = p.onChassisDim;
    ctx.fillText(data.subtitle, pad, 178);

    // ---- the LCD: the bill, the biggest thing on the card
    var lcdY = 218;
    var lcdH = 300;
    var alarm = data.severity === 'danger';
    ctx.fillStyle = alarm ? p.lcdBgAlarm : p.lcdBg;
    roundRect(ctx, pad, lcdY, W - pad * 2, lcdH, 8);
    ctx.fill();
    ctx.strokeStyle = p.chassisDeep;
    ctx.lineWidth = 2;
    ctx.stroke();

    var ink = alarm ? p.lcdInkAlarm : p.lcdInk;

    ctx.font = '500 26px ' + F.ui;
    ctx.fillStyle = ink;
    ctx.fillText(data.billLabel, pad + 36, lcdY + 44);

    // Draw Taka symbol in UI font so it scales nicely and doesn't break kerning
    ctx.font = '500 80px ' + F.ui;
    ctx.fillText('৳', pad + 36, lcdY + 208);
    var takaWidth = ctx.measureText('৳').width;

    // Draw the actual number in the digital font
    ctx.font = '700 132px ' + SEG;
    ctx.fillText(fmt(data.total), pad + 36 + takaWidth + 12, lcdY + 208);

    ctx.font = '400 26px ' + F.ui;
    ctx.fillText(data.unitsLine, pad + 36, lcdY + 272);

    // scanline, inside the LCD only — same 3% as the page
    ctx.save();
    roundRect(ctx, pad, lcdY, W - pad * 2, lcdH, 8);
    ctx.clip();
    ctx.fillStyle = 'rgba(0,0,0,0.03)';
    for (var sy = lcdY; sy < lcdY + lcdH; sy += 3) ctx.fillRect(pad, sy, W - pad * 2, 1);
    ctx.restore();

    var rowY = lcdY + lcdH + 76;

    function row(rowLabel, value, valueColour, valueFont, extraText, rightText) {
      ctx.font = '400 26px ' + F.ui;
      ctx.fillStyle = p.onChassisDim;
      ctx.fillText(rowLabel, pad, rowY);

      ctx.font = valueFont || ('700 46px ' + F.display);
      ctx.fillStyle = valueColour;
      ctx.fillText(value, pad, rowY + 58);

      if (rightText) {
        ctx.font = '400 24px ' + F.ui;
        ctx.fillStyle = p.onChassisDim;
        ctx.textAlign = 'right';
        ctx.fillText(rightText, W - pad, rowY + 58);
        ctx.textAlign = 'left';
      }

      var lineY = rowY + 92;
      if (extraText) {
        ctx.font = '400 22px ' + F.ui;
        ctx.fillStyle = p.savings;
        ctx.fillText(extraText, pad, lineY);
        lineY += 34;
      }

      ctx.strokeStyle = p.chassisEdge;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(pad, lineY);
      ctx.lineTo(W - pad, lineY);
      ctx.stroke();

      rowY = lineY + 56;
    }

    var topCostText = data.topCost > 0 ? data.topCostLine : null;
    row(data.topHogLabel, data.topName, alarm ? p.danger : p.brand, null, null, topCostText);

    if (data.savings > 0) {
      row(data.savingsOppLabel, data.savingsLine, p.savings, '700 46px ' + SEG);
    } else if (data.brandNames && data.brandNames.length) {
      // No in-catalog upgrade paid its way back, but the user's own fan is
      // thirsty enough that naming efficient brands IS the saving to point at.
      row(data.savingsOppLabel, data.brandNames.join('   ·   '), p.savings, '700 34px ' + F.display, data.brandNote);
    } else {
      row(data.savingsOppLabel, data.savingsNoneLabel, p.onChassisDim);
    }

    // ---- footer
    ctx.font = '400 22px ' + MONO;
    ctx.fillStyle = p.onChassisDim;
    ctx.fillText('alphaeshop.store', pad, H - 68);
    ctx.textAlign = 'right';
    ctx.font = '400 22px ' + F.ui;
    ctx.fillText(data.stamp, W - pad, H - 68);
    ctx.textAlign = 'left';
  }

  /* --------------------------------------------------------------- data */

  /** Everything the card needs, pulled from the same modules the page uses. */
  function collect() {
    if (!root.EC || !root.EC_WIZARD) return null;
    var state = root.EC_WIZARD.state;
    var sel = state.appliances || [];
    var units = root.EC.totalUnits(sel);
    if (!(units > 0)) return null;

    var bill = root.EC.calcBill(root.EC_WIZARD.getBillInputs(units));
    var myth = root.EC.applianceBreakdown(sel, bill.marginalRate);
    var top = myth.rows[0] || null;
    var cat = root.EC.getCategory(state.company, state.category);

    // The savings figure is whatever the recommendation engine already
    // computed for the page — the card never runs its own maths, so it can
    // never disagree with what the user just read.
    var savings = 0;
    try {
      if (root.EC_RECOMMEND && typeof root.EC_RECOMMEND.lastTotals === 'function') {
        var t = root.EC_RECOMMEND.lastTotals();
        if (t && isFinite(t.monthlySaved)) savings = t.monthlySaved;
      }
    } catch (e) {
      savings = 0;
    }

    var now = new Date();
    var t = i18n().t;
    var locale = i18n().getLocale();
    var companyDef = root.EC_RATES.companies[state.company];
    var topName = top ? label(top) : '—';

    // Brand signpost for the report card's savings-opportunity row: the first
    // appliance on the usage list whose OWN entered wattage is high enough to
    // name specific efficient brands (see brandRecommendations in
    // ec-rates-config.js). Scans every row, not just the top hog — a thirsty
    // 80W+ ceiling fan is worth flagging even when an AC outdraws it overall.
    var brandNames = null;
    var brandForName = null;
    for (var r = 0; r < myth.rows.length && !brandNames; r++) {
      var mrow = myth.rows[r];
      var mdef = root.EC.getAppliance(mrow.key);
      if (!mdef || !mdef.brandRecommendations) continue;
      for (var bi = 0; bi < mdef.brandRecommendations.length; bi++) {
        var bentry = mdef.brandRecommendations[bi];
        if (mrow.watts >= bentry.minWatts) {
          brandNames = bentry.brands.map(function (b) { return b.name; });
          brandForName = label(mrow);
          break;
        }
      }
    }

    return {
      locale: locale,
      total: bill.grandTotal,
      units: units,
      marginal: bill.marginalRate,
      severity: root.EC.severityFor(bill.marginalIndex, cat),
      topName: topName,
      topCost: top ? top.cost : 0,
      savings: savings,
      brandNames: brandNames,
      brandNote: brandNames ? t('card.brandHintNote', { name: brandForName }) : null,
      subtitle: companyDef
        ? t('card.subtitle', { company: label(companyDef.name), category: state.category })
        : state.category,
      stamp: now.getFullYear() + '-' + String(now.getMonth() + 1).padStart(2, '0') +
        '-' + String(now.getDate()).padStart(2, '0'),
      // Pre-formatted strings so draw() stays pure text-placement — no locale
      // lookups inside the drawing code itself.
      title: t('card.title'),
      billLabel: t('card.billLabel'),
      unitsLine: fmt(units) + ' ' + t('meter.units') + ' · ' +
        t('step3.bill.marginal.pre') + bill.marginalRate.toFixed(2),
      topHogLabel: t('card.topHog'),
      topCostLine: t('card.monthlyCost', { cost: fmt(top ? top.cost : 0) }),
      savingsOppLabel: t('card.savingsOpp'),
      savingsLine: t('card.savingsPerMonth', { amount: fmt(savings) }),
      savingsNoneLabel: t('card.savingsNone')
    };
  }

  /* ------------------------------------------------------------ download */

  function setStatus(msg, isError) {
    if (!els.status) return;
    els.status.textContent = msg || '';
    els.status.hidden = !msg;
    if (isError) els.status.setAttribute('data-tone', 'cost');
    else els.status.removeAttribute('data-tone');
  }

  function download(blob) {
    // Revoke the previous URL before minting a new one — a user who taps the
    // button repeatedly should not leak a blob per tap.
    if (lastObjectUrl) {
      try { URL.revokeObjectURL(lastObjectUrl); } catch (e) {}
      lastObjectUrl = null;
    }
    var url = URL.createObjectURL(blob);
    lastObjectUrl = url;

    var a = document.createElement('a');
    a.href = url;
    a.download = i18n().t('card.filename');
    document.body.appendChild(a);
    a.click();
    a.remove();

    // Give the browser time to start the download before releasing the URL.
    setTimeout(function () {
      if (lastObjectUrl !== url) return;
      try { URL.revokeObjectURL(url); } catch (e) {}
      lastObjectUrl = null;
    }, 60000);
  }

  function generate() {
    setStatus(i18n().t('card.status.generating'));
    if (els.btn) els.btn.disabled = true;

    function fail(reason) {
      // NEVER throw into the page. The results stay exactly as they are; the
      // card is simply not offered.
      if (root.console) console.warn('[ec-card] ' + reason);
      setStatus(i18n().t('card.status.failed'), true);
      if (els.btn) els.btn.disabled = false;
    }

    fontsReady().then(function () {
      try {
        var data = collect();
        if (!data) return fail('no appliances selected');

        var canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        var ctx = canvas.getContext && canvas.getContext('2d');
        if (!ctx) return fail('no 2d context');

        // Only the Bengali card draws Bengali glyphs — an English card has
        // nothing to check here, so a device without Bengali coverage still
        // gets its (correct, English) card.
        if (data.locale === 'bn' && !bengaliRenders(ctx)) {
          return fail('Bengali glyphs unavailable on this device');
        }

        draw(ctx, data, palette());

        if (!canvas.toBlob) {
          return fail('canvas.toBlob unsupported');
        }

        canvas.toBlob(function (blob) {
          if (!blob) return fail('toBlob returned nothing');
          try {
            download(blob);
            setStatus(i18n().t('card.status.done'));
          } catch (e) {
            fail('download failed: ' + e.message);
            return;
          }
          if (els.btn) els.btn.disabled = false;
        }, 'image/png');
      } catch (e) {
        fail('draw failed: ' + e.message);
      }
    }, function () {
      fail('fonts never settled');
    });
  }

  /* --------------------------------------------------------------- boot */

  function syncAvailability() {
    if (!els.section) return;
    // The card is a reward for finishing. With nothing selected there is
    // nothing to reward, so the whole beat stays out of the way. Same for a
    // non-LT-A customer class (Run 4 Phase 4) — collect() calls EC.calcBill(),
    // which throws for any category that isn't LT-A, so there is no bill to
    // put on a card at all.
    var functional = !(root.EC_WIZARD && root.EC_WIZARD.categoryFunctional) ||
      root.EC_WIZARD.categoryFunctional();
    var has = !!(root.EC && root.EC_WIZARD && functional &&
      root.EC.totalUnits(root.EC_WIZARD.state.appliances || []) > 0);
    els.section.hidden = !has;
  }

  function init() {
    if (booted) return;
    els.section = $('[data-ec-cardgen]');
    if (!els.section) return;

    els.btn = $('[data-ec-card-download]');
    els.status = $('[data-ec-card-status]');
    booted = true;

    if (els.btn) els.btn.addEventListener('click', generate);

    if (root.EC_WIZARD && root.EC_WIZARD.on) {
      root.EC_WIZARD.on('change', syncAvailability);
      // Appliance edits fire `recalc`, not `change` — without this the beat
      // stays hidden after the user picks their first appliance.
      root.EC_WIZARD.on('recalc', syncAvailability);
    }
    syncAvailability();
  }

  root.EC_CARD = { init: init, generate: generate, _draw: draw, _collect: collect };

  function boot() {
    if (root.EC_I18N && root.EC_I18N.whenReady) root.EC_I18N.whenReady(init);
    else init();
  }

  if (typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', boot);
    } else {
      boot();
    }
  }
})(typeof globalThis !== 'undefined' ? globalThis : this);
